import React, { useContext, useEffect, useState } from 'react';

import ComparisonContext from '../contexts/ComparisonContext';
import DateInputContext from '../contexts/DateInputContext';
import DateInputProvider from '../contextProviders/DateInputProvider';

/**
 * Syncs DateInputContext state from the parent report's date context into this
 * gadget's inner DateInputProvider. DateInputProvider uses useState(initial*)
 * which only reads the initial value on mount — subsequent changes from the
 * parent (e.g. user picking a new date in the popup report) are ignored without
 * this sync.
 */
const DateContextSync = ({
  children,
  dateField,
  dateRange,
  relativeDateRange,
  advancedRelativeDateRange,
}: {
  children: JSX.Element | JSX.Element[];
  dateField: string;
  dateRange: DateRangeInput | undefined;
  relativeDateRange: RelativeDateRange | undefined;
  advancedRelativeDateRange: AdvancedRelativeDateRange | undefined;
}) => {
  const {
    setDateField,
    setDateRange,
    setRelativeDateRange,
    setAdvancedRelativeDateRange,
  } = useContext(DateInputContext);

  useEffect(() => {
    setDateField(dateField);
  }, [dateField, setDateField]);

  useEffect(() => {
    setDateRange(dateRange);
  }, [dateRange, setDateRange]);

  useEffect(() => {
    setRelativeDateRange(relativeDateRange);
  }, [relativeDateRange, setRelativeDateRange]);

  useEffect(() => {
    setAdvancedRelativeDateRange(advancedRelativeDateRange);
  }, [advancedRelativeDateRange, setAdvancedRelativeDateRange]);

  return <>{children}</>;
};

const ReportGadgetDateScopeProvider = ({
  children,
  reportGadget,
}: {
  children: JSX.Element | JSX.Element[];
  reportGadget: ReportGadgets.ReportGadget;
}) => {
  // Get parent report's date scope and comparison.
  // The parent comparison is used as a fallback when the gadget has no
  // comparison of its own — otherwise wrapping in this provider would override
  // the report-level comparison with undefined.
  const parentDateContext = useContext(DateInputContext);
  const { currentComparison: parentComparison } = useContext(ComparisonContext);

  const [currentComparison, setCurrentComparison] = useState<
    PersistedComparisonType | undefined
  >(reportGadget.comparison ?? parentComparison);

  useEffect(() => {
    setCurrentComparison(reportGadget.comparison ?? parentComparison);
  }, [reportGadget.comparison, parentComparison]);

  // Only override dateField if the gadget has a custom one
  // Date scope (dateRange, relativeDateRange, etc.) always comes from parent
  const dateFieldToUse = reportGadget.dateField || parentDateContext.dateField;

  return (
    <DateInputProvider
      initialDateRange={parentDateContext.dateRange}
      initialRelativeDateRange={parentDateContext.relativeDateRange}
      initialAdvancedRelativeDateRange={
        parentDateContext.advancedRelativeDateRange
      }
      initialDateField={dateFieldToUse}
    >
      <DateContextSync
        dateField={dateFieldToUse}
        dateRange={parentDateContext.dateRange}
        relativeDateRange={parentDateContext.relativeDateRange}
        advancedRelativeDateRange={parentDateContext.advancedRelativeDateRange}
      >
        <ComparisonContext.Provider
          value={{ currentComparison, setCurrentComparison }}
        >
          {children}
        </ComparisonContext.Provider>
      </DateContextSync>
    </DateInputProvider>
  );
};

export default ReportGadgetDateScopeProvider;
